// ============================================================================
// STATISTIQUES › STOCK › STOCK PAR PÉRIODES — l'état du stock période après période (semaine, mois, année), puis celui
// d'aujourd'hui, sur les niveaux que l'on choisit. Réf. maquette du back-office (écran `stp`, « Statistiques · Stock par
// périodes ») et docs/CADRAGE-Statistiques-Stock.md §11.
//
// ⚠️ L'ÉCRAN NOMME ET DESSINE, IL NE DÉFINIT AUCUN NOMBRE : les colonnes (leur période, le jour où chacune se lit), le stock
//    de leur fin et l'état du jour viennent du serveur (`sales/stats-stock-periods.ts`). Le nom d'une période vient d'`Intl`
//    dans la langue du compte.
// ⚠️ UN STOCK EST UN ÉTAT : la dernière colonne n'est pas une somme, c'est aujourd'hui.
// ⚠️ ZÉRO ET UN STOCK NÉGATIF S'AFFICHENT TELS QUELS (décision utilisateur — le dessin les confond en « — ») ; une valeur
//    non mesurable s'écrit « — ». Un stock négatif n'a pas de valeur à montrer.
// ⚠️ LE BOUTON « Stock — qté puis € HT » DU DESSIN NE FAISAIT RIEN : il devient une étiquette (décision utilisateur).
// ============================================================================
import type { ReactNode } from 'react';
import { Trans } from '@orgazone/ui-web';
import { currentCurrency, toInputValue } from '@orgazone/money-format';
import type { SalesSummaryFilterKey, StockPeriodCell, StockPeriodGrain, StockPeriodsView } from '../api';
import { key, t, type Translatable } from '../i18n';
import { exporterCsv } from './export-csv';
import { FILTER_DIMS, dimLabel } from './stats-bar';
import { csvQty, eur0, qty, shownValue } from './stats-format';
import { StatsGrid, sortStatsRows, type GridCell, type GridColumn, type StatsSort } from './stats-grid';

/** Le tri d'ouverture : l'ordre des niveaux, à égalité le plus gros stock d'aujourd'hui d'abord. */
export const STOCK_PERIODS_DEFAULT_SORT: StatsSort = { k: 'today', d: -1, man: false };

const TODAY_KEY = 'today';

// ─── Les noms des colonnes ───────────────────────────────────────────────────────────────────────

/** `AAAAMMJJ` → une date à midi UTC : le jour ne bascule pas selon le fuseau de qui regarde. */
const dateOf = (ymd: string): Date => new Date(Date.UTC(+ymd.slice(0, 4), +ymd.slice(4, 6) - 1, +ymd.slice(6, 8), 12));

type Bucket = StockPeriodsView['buckets'][number];

/**
 * Le nom d'une colonne et son surtitre — « S36 » et « 31/08→06/09 », « sept. » et « 26 », « 2026 » et « année ».
 * ⚠️ DÉRIVÉS D'`Intl` DANS LA LANGUE DU COMPTE, appelés au rendu : un calendrier écrit à la main ne parlerait que français.
 */
export function bucketNames(b: Bucket, grain: StockPeriodGrain, lang: string): { label: string; sub: string } {
  if (grain === 'w') {
    const short = new Intl.DateTimeFormat(lang, { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
    return { label: t('S{numero}|semaine', { numero: b.isoWeek ?? 0 }), sub: `${short.format(dateOf(b.from))}→${short.format(dateOf(b.to))}` };
  }
  if (grain === 'm') {
    return {
      label: new Intl.DateTimeFormat(lang, { month: 'short', timeZone: 'UTC' }).format(dateOf(b.from)),
      sub: b.from.slice(2, 4),
    };
  }
  return { label: b.from.slice(0, 4), sub: t('année') };
}

// ─── Les réglages de l'onglet ────────────────────────────────────────────────────────────────────

const GRAINS: { v: StockPeriodGrain; label: Translatable; title: Translatable }[] = [
  { v: 'w', label: key('Semaine'), title: key('Une colonne par semaine ISO') },
  { v: 'm', label: key('Mois'), title: key('Une colonne par mois') },
  { v: 'y', label: key('Année'), title: key('Une colonne par année') },
];

/**
 * Au bout de la ligne PÉRIODE : ce que l'écran montre (une étiquette, plus un bouton) et « Éclater par » Semaine · Mois ·
 * Année.
 */
export function StockPeriodsControls({ grain, onGrain }: { grain: StockPeriodGrain; onGrain: (g: StockPeriodGrain) => void }) {
  return (
    <>
      <span title={t('Cet écran montre le stock : quantité puis valeur')} style={{
        height: 30, padding: '0 11px', border: '1.5px solid var(--teal-500)', borderRadius: 'var(--r-pill)', background: 'var(--paper)',
        color: 'var(--teal-500)', fontSize: 'var(--t-body)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 7,
        whiteSpace: 'nowrap', boxSizing: 'border-box',
      }}>
        <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)', flexShrink: 0 }} />
        {t('Stock — qté puis {symbole} HT', { symbole: currentCurrency().symbol })}
      </span>
      <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--ink-200)', margin: '0 2px' }} />
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)',
        whiteSpace: 'nowrap',
      }}>{t('Éclater par')}</span>
      <span role="group" aria-label={t('Éclater par')} style={{
        display: 'inline-flex', padding: 2, gap: 2, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)',
      }}>
        {GRAINS.map((x) => {
          const on = x.v === grain;
          return (
            <button key={x.v} type="button" aria-pressed={on} title={t(x.title)} onClick={() => onGrain(x.v)} style={{
              height: 26, padding: '0 10px', border: 'none', borderRadius: 'var(--r-xs)', background: on ? 'var(--paper)' : 'transparent',
              color: on ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: on ? '0 1px 4px rgba(12,14,16,.14)' : 'none',
              fontSize: 'var(--t-note)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
            }}>{t(x.label)}</button>
          );
        })}
      </span>
    </>
  );
}

// ─── Le tri et les cellules ──────────────────────────────────────────────────────────────────────

type Row = StockPeriodsView['rows'][number];

/** Le stock d'une ligne dans une colonne (une période, ou aujourd'hui). */
const cellAt = (r: { cells: StockPeriodCell[]; today: StockPeriodCell }, k: string, view: StockPeriodsView): StockPeriodCell | null =>
  (k === TODAY_KEY ? r.today : r.cells[view.buckets.findIndex((b) => b.key === k)] ?? null);

/** Les lignes dans l'ordre demandé — à égalité de niveaux, le plus gros stock d'aujourd'hui d'abord. */
export function sortStockPeriodRows(view: StockPeriodsView, sort: StatsSort, lang: string): Row[] {
  return sortStatsRows(view.rows, view.axes.length, sort, (r, k) => cellAt(r, k, view)?.qty ?? null, (x, y) => y.today.qty - x.today.qty, lang);
}

/**
 * Une cellule : la quantité, puis sa valeur. ⚠️ Zéro se dit « 0 », sans valeur ; un stock négatif se montre, sans valeur à
 * valoriser ; une valeur non mesurable s'écrit « — ».
 */
function cell(c: StockPeriodCell, strong: boolean): GridCell {
  const fw = strong ? 800 : 700;
  if (c.qty === 0) return { text: qty(0), fg: 'var(--ink-300)', fw };
  if (c.qty < 0) return { text: qty(c.qty), fg: 'var(--ink-900)', fw };
  return { text: qty(c.qty), fg: 'var(--ink-900)', fw, sub: { text: eur0(c.valueCents), fg: 'var(--ink-500)' } };
}

/** Les colonnes : une par période, puis aujourd'hui. */
function columnsOf(view: StockPeriodsView, lang: string): GridColumn[] {
  return [
    ...view.buckets.map((b): GridColumn => {
      const names = bucketNames(b, view.grain, lang);
      return {
        k: b.key, label: names.label, sub: names.sub, width: 92, track: 'minmax(92px,1fr)', color: 'var(--teal-500)', headFg: 'var(--ink-800)',
        title: t('Trier sur cette période'),
      };
    }),
    {
      k: TODAY_KEY, label: t('Aujourd’hui'), sub: t('état actuel'), width: 124, track: '124px', color: 'var(--ink-700)', headBg: 'var(--ink-50)',
      headFg: 'var(--ink-900)', title: t('Trier sur le stock d’aujourd’hui'),
    },
  ];
}

// ─── Le tableau ──────────────────────────────────────────────────────────────────────────────────

/**
 * LE TABLEAU DU STOCK PAR PÉRIODES — une colonne par période et celle d'aujourd'hui, dans le squelette commun.
 * `filtered` dit si un filtre est posé : l'état vide ne dit pas la même chose avec ou sans.
 */
export function StockPeriodsTable({ view, sort, onSort, filtered, onReset, lang, busy = false }: {
  view: StockPeriodsView;
  sort: StatsSort;
  onSort: (next: StatsSort) => void;
  filtered: boolean;
  onReset: () => void;
  lang: string;
  busy?: boolean;
}) {
  const rows = sortStockPeriodRows(view, sort, lang);
  const axisDim = (k: SalesSummaryFilterKey) => FILTER_DIMS.find((d) => d.k === k)!;
  const totalSub = [t('{n} ligne', { n: view.rows.length }), ...(sort.man ? [] : [t('ordre de la ventilation')])].join(' · ');
  const strong = (x: ReactNode) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>;

  return (
    <StatsGrid label={t('Stock par périodes')} sort={sort} onSort={onSort} busy={busy} stacked lastAxis={null} totalSubSize="var(--t-meta)"
      axes={view.axes.map((k) => ({ label: dimLabel(axisDim(k)), code: k === 'art' }))}
      columns={columnsOf(view, lang)}
      total={{ sub: totalSub, cells: [...view.total.cells.map((c) => cell(c, true)), cell(view.total.today, true)] }}
      rows={rows.map((r) => ({
        key: r.parts.map((p) => p.value).join('\u001f'), parts: r.parts, sub: t('{n} article', { n: r.articles }),
        cells: [...r.cells.map((c) => cell(c, false)), cell(r.today, true)],
      }))}
      after={(
        <>
          {view.rows.length === 0 && (
            <div style={{ padding: '34px 18px', textAlign: 'center' }}>
              {filtered ? (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun article ne correspond à ces filtres')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>
                    <Trans k='Les filtres se cumulent en ET — <1>effacez-les</1> pour repartir.' parts={[(x) => (
                      <button type="button" onClick={onReset} style={{
                        border: 'none', background: 'transparent', padding: 0, color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700,
                        textDecoration: 'underline', cursor: 'pointer',
                      }}>{x}</button>
                    )]} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Ni stock ni mouvement sur cette période')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Choisissez une autre période.')}</div>
                </>
              )}
            </div>
          )}
          {view.truncated && (
            // ⚠️ LA COUPURE SE DIT : un tableau qui s'arrête en silence ferait croire qu'il couvre toute la période choisie.
            <div role="note" style={{
              padding: '9px 16px', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-note)', fontWeight: 600,
              borderTop: '1px solid var(--ink-200)',
            }}>
              {t('{n} seule période est montrée : choisissez une période plus courte ou un grain plus large.', { n: view.buckets.length })}
            </div>
          )}
          <div style={{
            padding: '10px 16px', background: 'var(--ink-50)', borderRadius: '0 0 var(--r-sm) var(--r-sm)', fontSize: 'var(--t-meta)',
            color: 'var(--ink-500)', lineHeight: 1.55,
          }}>
            <Trans k='Chaque colonne porte l’état du stock à la <1>fin de la période</1> : la quantité, puis sa valeur {symbole} HT au PMP de la date. La colonne de droite n’est pas une somme — c’est l’état <2>d’aujourd’hui</2> : un stock ne s’additionne pas dans le temps. Une période en cours se lit à date ; zéro et un stock négatif s’affichent tels quels, une valeur non mesurable « — ».'
              vars={{ symbole: currentCurrency().symbol }} parts={[strong, strong]} />
          </div>
        </>
      )} />
  );
}

// ─── L'export ────────────────────────────────────────────────────────────────────────────────────

/**
 * « ON EXPORTE CE QUE L'ON VOIT » : les niveaux, chaque colonne en deux colonnes du fichier (pièces, valeur), aujourd'hui,
 * le tri, la ligne de total. ⚠️ Une valeur inconnue reste VIDE ; celle d'un stock nul ou négatif aussi, comme à l'écran.
 */
export function exportStockPeriods(view: StockPeriodsView, sort: StatsSort, lang: string): void {
  const currency = currentCurrency().code;
  const heads = [
    ...view.buckets.map((b) => { const n = bucketNames(b, view.grain, lang); return `${n.label} ${n.sub}`; }),
    t('Aujourd’hui'),
  ].flatMap((h) => [h, t('{v} — valeur ({devise})', { v: h, devise: currency })]);
  const values = (cells: StockPeriodCell[]): string[] => cells.flatMap((c) => [
    csvQty(c.qty), c.qty > 0 && c.valueCents !== null ? toInputValue(c.valueCents) : '',
  ]);
  const rows = sortStockPeriodRows(view, sort, lang);
  exporterCsv(`stock-par-periodes-${view.grain}-${view.period.from}-${view.period.to}`,
    [...view.axes.map((k) => dimLabel(FILTER_DIMS.find((d) => d.k === k)!)), t('Articles'), ...heads],
    [
      [t('TOTAL'), ...view.axes.slice(1).map(() => ''), String(view.scope.references), ...values([...view.total.cells, view.total.today])],
      ...rows.map((r) => [
        ...r.parts.map((p) => (p.label !== p.value ? `${p.value} — ${shownValue(p.label)}` : shownValue(p.value))), String(r.articles),
        ...values([...r.cells, r.today]),
      ]),
    ]);
}
